import { useCallback } from 'react';
import type { Price } from 'types/capi/shared';

import type { StockStatus } from '~/domain/stockStatus';
import NotificationImpressionEvent, {
  type NotificationImpressionElement
} from '~/events/NotificationImpressionEvent';
import type { NotificationPlacement } from '~/events/NotificationInteractionEvent';
import useImpression from '~/hooks/useImpression';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';

type Options = {
  element: NotificationImpressionElement;
  placement: NotificationPlacement;
  price: Price;
  productId: number;
  stockStatus?: StockStatus;
};

// Once per element per view: the button can change what it offers while it is on screen, and each
// offer is the denominator for its own taps.
export default function useNotificationImpression<T extends HTMLElement>({
  element,
  placement,
  price,
  productId,
  stockStatus
}: Options) {
  const { isBot } = useRootData();
  const { guestId } = useUser();

  const track = useCallback(() => {
    new NotificationImpressionEvent({
      element,
      guestId,
      isBot,
      placement,
      price,
      productId,
      stockStatus
    }).track();
  }, [element, guestId, isBot, placement, price, productId, stockStatus]);

  return useImpression<T>(track, { enabled: !isBot, variant: element });
}
